import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DatabaseBackup, Download, HardDriveDownload, Cloud, CloudOff, Loader2, History, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`);
const fmtDate = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

export default function SauvegardesPage() {
  const [history, setHistory] = useState([]);
  const [drive, setDrive] = useState({ configured: false, connected: false });
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/backup/history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/backup/drive/status").then(({ data }) => setDrive(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("drive_connected")) {
      toast.success("Google Drive connecté ✓ Les sauvegardes nocturnes seront envoyées sur le Drive.");
    }
  }, []);

  const download = async (format) => {
    setBusy(format);
    try {
      const resp = await api.get(`/backup/export`, { params: { format }, responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mazid_backup_${format}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'export"); }
    finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/backup/run");
      toast.success(`Sauvegarde effectuée ✓ (${data.collections} collections · Drive : ${data.drive_status === "envoye" ? "envoyée" : data.drive_status})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/backup/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Clés Google non configurées"); }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("Déconnecter Google Drive ?")) return;
    await api.delete("/backup/drive/disconnect");
    toast.success("Google Drive déconnecté");
    load();
  };

  return (
    <div className="space-y-6" data-testid="sauvegardes-page">
      <div>
        <div className="chip mb-2"><ShieldCheck className="h-3 w-3" /><span>PROTECTION DES DONNÉES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Sauvegardes</h1>
        <p className="text-slate-500 mt-1">Export complet de la base en un clic + sauvegarde automatique chaque nuit à 00h00 GMT.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2 mb-1">
            <HardDriveDownload className="h-4 w-4" />Télécharger maintenant
          </div>
          <p className="text-sm text-slate-500 mb-4">Sauvegarde complète de toutes les données (élèves, paiements, notes, utilisateurs...).</p>
          <div className="flex gap-2">
            <Button onClick={() => download("json")} disabled={!!busy} className="bg-[#1E3A8A] text-white gap-1 flex-1" data-testid="backup-download-json">
              {busy === "json" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}JSON
            </Button>
            <Button onClick={() => download("csv")} disabled={!!busy} variant="outline" className="gap-1 flex-1 border-[#1E3A8A] text-[#1E3A8A]" data-testid="backup-download-csv">
              {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}CSV
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2 mb-1">
            <DatabaseBackup className="h-4 w-4" />Sauvegarde serveur
          </div>
          <p className="text-sm text-slate-500 mb-4">Génère une sauvegarde, la stocke en lieu sûr et l&apos;envoie sur Google Drive (si connecté).</p>
          <Button onClick={runNow} disabled={!!busy} className="bg-[#166534] text-white gap-1 w-full" data-testid="backup-run-now">
            {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}Sauvegarder maintenant
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="drive-card">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2 mb-1">
            {drive.connected ? <Cloud className="h-4 w-4 text-green-600" /> : <CloudOff className="h-4 w-4 text-slate-400" />}Google Drive
          </div>
          {drive.connected ? (
            <>
              <p className="text-sm text-green-700 mb-1 font-semibold">Connecté ✓ (sorytiranke3@gmail.com)</p>
              <p className="text-xs text-slate-500 mb-3">Envoi automatique chaque nuit à 00h00 GMT dans le dossier « Sauvegardes MAZID ».</p>
              <Button onClick={disconnectDrive} variant="outline" size="sm" className="text-red-600" data-testid="drive-disconnect">Déconnecter</Button>
            </>
          ) : drive.configured ? (
            <>
              <p className="text-sm text-slate-500 mb-3">Autorisez l&apos;accès au Drive de sorytiranke3@gmail.com pour activer l&apos;envoi nocturne.</p>
              <Button onClick={connectDrive} className="bg-[#EA580C] text-white gap-1 w-full" data-testid="drive-connect">
                <Cloud className="h-4 w-4" />Connecter Google Drive
              </Button>
            </>
          ) : (
            <p className="text-sm text-slate-500">Clés Google non configurées. Fournissez le Client ID et le Client Secret (Google Cloud Console) pour activer l&apos;envoi vers Drive. La sauvegarde nocturne locale reste active.</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2 mb-3">
          <History className="h-4 w-4" />Historique des sauvegardes
        </div>
        {history.length === 0 ? (
          <div className="text-slate-400 text-sm py-6 text-center" data-testid="backup-history-empty">Aucune sauvegarde pour l&apos;instant.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-history-table">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-4">Date</th><th className="pr-4">Type</th><th className="pr-4">Format</th>
                  <th className="pr-4">Taille</th><th className="pr-4">Collections</th><th className="pr-4">Documents</th><th>Google Drive</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                    <td className="pr-4 capitalize">{h.type}</td>
                    <td className="pr-4 uppercase">{h.format}</td>
                    <td className="pr-4">{fmtSize(h.size)}</td>
                    <td className="pr-4">{h.collections}</td>
                    <td className="pr-4">{h.documents}</td>
                    <td>{h.drive_status === "envoye" ? <span className="text-green-700 font-semibold">Envoyée ✓</span>
                      : h.drive_status === "non_connecte" ? <span className="text-slate-400">Non connecté</span>
                      : h.drive_status ? <span className="text-red-600">{h.drive_status}</span>
                      : <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ZONE DANGER : Passage en production réelle */}
      <ProductionResetCard />
    </div>
  );
}

function ProductionResetCard() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [purgeStructure, setPurgeStructure] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [activating, setActivating] = useState(false);

  const activateProdMode = async () => {
    if (!window.confirm("Activer le MODE PRODUCTION (sans purge des données) ?\n- Codes OTP de debug masqués\n- Paiements de démonstration bloqués\n- Comptes démo supprimés\n- Chiffres réels sur le site public")) return;
    setActivating(true);
    try {
      const { data } = await api.post("/system/production-mode", { enabled: true });
      toast.success(`Mode production activé${data.demo_accounts_deleted ? ` — ${data.demo_accounts_deleted} compte(s) démo supprimé(s)` : ""}`);
      setStatus((s) => ({ ...(s || {}), production_mode: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    api.get("/admin/production-status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/production-reset",
        { password, confirmation: confirm, purge_structure: purgeStructure }, { timeout: 300000 });
      setResult(data);
      setOpen(false);
      toast.success(`Passage en production effectué : ${data.total_deleted} documents de démonstration supprimés. Sauvegarde : ${data.backup_file}`);
      setStatus((s) => ({ ...(s || {}), production_mode: true, students_count: 0 }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du passage en production");
    } finally {
      setRunning(false);
    }
  };

  if (!status) return null;
  return (
    <div className="bg-white rounded-xl border-2 border-red-200 p-6" data-testid="production-reset-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading font-bold text-lg text-red-700">Zone critique — Passage en production réelle</h2>
          {status.production_mode ? (
            <p className="text-sm text-green-700 mt-1 font-semibold" data-testid="production-mode-badge">
              ✓ Application en PRODUCTION RÉELLE depuis le {status.reset_at ? fmtDate(status.reset_at) : "—"} (par {status.reset_by || "—"})
            </p>
          ) : (
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Supprime TOUTES les données de démonstration ({status.students_count} élèves actuels, paiements, notes, présences...)
              pour que la direction reprenne l&apos;application avec des données vierges.
              Conserve : comptes de direction, configuration école, plan comptable, bibliothèque numérique, paramètres.
              Une sauvegarde complète est faite automatiquement AVANT la purge.
            </p>
          )}
        </div>
        {!status.production_mode && (
          <div className="flex flex-col gap-2 items-end">
            <button onClick={activateProdMode} disabled={activating}
              className="px-4 py-2 rounded-lg bg-green-700 text-white text-sm font-bold hover:bg-green-800 disabled:opacity-50"
              data-testid="production-mode-toggle-btn">
              {activating ? "Activation..." : "Activer le mode production (sans purge)"}
            </button>
            <button onClick={() => setOpen(true)}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700"
            data-testid="production-reset-open-btn">
            Passer en production réelle (avec purge)
          </button>
          </div>
        )}
      </div>

      {result && (
        <div className="mt-4 text-xs bg-green-50 border border-green-200 rounded-lg p-3" data-testid="production-reset-result">
          <b>{result.total_deleted}</b> documents supprimés — sauvegarde de sécurité : <b>{result.backup_file}</b>.
          Conservé : {result.kept}. Vous pouvez maintenant programmer les ouvertures de classes (Pédagogie → Classes) et les grilles tarifaires.
        </div>
      )}

      {open && (
        <div className="mt-5 border-t border-red-100 pt-5 space-y-3 max-w-lg">
          <label className="block text-sm">
            <span className="font-semibold">1. Tapez <span className="font-mono text-red-700">PRODUCTION</span> pour confirmer :</span>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="PRODUCTION"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="production-reset-confirm-input" />
          </label>
          <label className="block text-sm">
            <span className="font-semibold">2. Votre mot de passe :</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="production-reset-password-input" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={purgeStructure} onChange={(e) => setPurgeStructure(e.target.checked)}
              data-testid="production-reset-structure-checkbox" />
            Supprimer aussi les classes, grilles tarifaires et emplois du temps (repartir de zéro)
          </label>
          <div className="flex gap-2">
            <button onClick={run} disabled={running || confirm.trim().toUpperCase() !== "PRODUCTION" || !password}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40"
              data-testid="production-reset-run-btn">
              {running ? "Purge en cours (sauvegarde automatique)..." : "Confirmer la purge définitive"}
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-sm font-semibold">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}
